/**
 * Dependencies.
 */
import { BaseError } from '@prz-dp/services';
import { Component, Mixins } from 'vue-property-decorator';

import AddressModel from '@/api/address/address.model';
import { orderRequestService } from '@/api/order-request/order-request.service';
import { formatAddressSingleLine } from '@/modules/main/utils/address';
import {
  ORDER_REQUEST_STATUS,
  ORDER_REQUEST_TYPES
} from '@/modules/tracking/constants';
import TimelineMixin from '@/modules/tracking/mixins/timeline.mixin';
import TrackingMixin from '@/modules/tracking/mixins/tracking.mixin';
import { trackingStore } from '@/modules/tracking/store';
import Error from '@/ui/components/error.component.vue';
import NotifyClientRequest from '@/ui/components/notify/client-request.component.vue';
import Timeline from '@/ui/components/timeline/timeline.component.vue';

/**
 * Component.
 */
@Component({
  components: { Error, NotifyClientRequest, Timeline },
  name: 'request-cancel-scene'
})
export default class extends Mixins(TrackingMixin, TimelineMixin) {
  /**
   * Property `ORDER_REQUEST_STATUS`.
   */
  ORDER_REQUEST_STATUS = ORDER_REQUEST_STATUS;

  /**
   * Property `isSnackbarVisible`.
   */
  isSnackbarVisible = false;

  /**
   * Property `isDeliveryRequestCanceledSuccessfully`.
   */
  isDeliveryRequestCanceledSuccessfully = true;

  /**
   * Getter `requestDetail`.
   */
  get requestDetail() {
    const requests = trackingStore.requests || [];
    const latestRequest = requests.find(
      ({ status: { code } = {}, type }) =>
        code === ORDER_REQUEST_STATUS.PENDING_TREATMENT &&
        (type === ORDER_REQUEST_TYPES.CHANGE_ADDRESS ||
          type === ORDER_REQUEST_TYPES.CHANGE_DATE ||
          type === ORDER_REQUEST_TYPES.CHANGE_DATE_AND_ADDRESS)
    );
    const {
      recordDate,
      request = { from: null, to: null },
      status = { code: null },
      statusDate,
      type
    } = latestRequest || {};

    return { recordDate, request, status, statusDate, type };
  }

  /**
   * Getter `hasPendingRequests`.
   */
  get hasPendingRequests() {
    const {
      changeAddress = false,
      changeDate = false,
      changeDateAndAddress = false,
      contact = false
    } = this.requestPendingTreatment || {};

    return changeAddress || changeDate || changeDateAndAddress || contact;
  }

  /**
   * Getter `hasPendingAddressOrDateChange`.
   */
  get hasPendingAddressOrDateChange() {
    const { type } = this.$route.params || {};

    return type === 'request';
  }

  /**
   * Lifecycle mounted hook.
   */
  async mounted() {
    this.$on('trackingDataUpdated', () => {
      const isValid = this.handleIsValidToBeOnScene();

      if (!isValid) {
        this.handleForceTrackingScene();
      }
    });

    await this.handleInitializeMixin();

    const isValid = this.handleIsValidToBeOnScene();

    if (!isValid) {
      this.handleForceTrackingScene();
    }
  }

  /**
   * Handle is valid to be on scene.
   */
  handleIsValidToBeOnScene() {
    const { type } = this.$route.params || {};
    const deliveryRequestId = this.handleFindRequestId({ type });

    return !!deliveryRequestId && this.isDelivery;
  }

  /**
   * Handle date.
   */
  handleDate(statusDate: string) {
    return statusDate ? this.$moment(statusDate).format('DD MMM YYYY') : '';
  }

  /**
   * Handle hour.
   */
  handleHour(statusDate: string) {
    return statusDate ? this.$moment(statusDate).format('HH:mm') : '';
  }

  /**
   * Handle build request detail.
   */
  handleBuildRequestDetail({
    detail = null,
    showAdditionalInformation = false,
    type = null
  }: {
    detail: { address?: AddressModel; date?: string };
    showAdditionalInformation?: boolean;
    type: string;
  }) {
    let dateString = '';
    let addressString = '';

    if (
      [
        ORDER_REQUEST_TYPES.CHANGE_DATE,
        ORDER_REQUEST_TYPES.CHANGE_DATE_AND_ADDRESS
      ].includes(type)
    ) {
      const { date = null } = detail || {};

      dateString = this.handleDate(date);
    }

    if (
      [
        ORDER_REQUEST_TYPES.CHANGE_ADDRESS,
        ORDER_REQUEST_TYPES.CHANGE_DATE_AND_ADDRESS
      ].includes(type)
    ) {
      const { address = null } = detail || {};

      addressString = formatAddressSingleLine({
        address,
        showAdditionalInformation
      });
    }

    return [dateString, addressString].filter(Boolean).join(' | ');
  }

  /**
   * Handle find request id.
   */
  handleFindRequestId({ type }: { type: string }) {
    let requestType = null;

    switch (type) {
      // if canceling contact type.
      case 'contact':
        requestType = ORDER_REQUEST_TYPES.CONTACT;
        break;

      // if canceling delivery type.
      case 'request':
        const {
          changeAddress = false,
          changeDate = false,
          changeDateAndAddress = false
        } = this.requestPendingTreatment || {};

        if (changeDate) {
          requestType = ORDER_REQUEST_TYPES.CHANGE_DATE;
        }

        if (changeAddress) {
          requestType = ORDER_REQUEST_TYPES.CHANGE_ADDRESS;
        }

        if (changeDateAndAddress) {
          requestType = ORDER_REQUEST_TYPES.CHANGE_DATE_AND_ADDRESS;
        }

        break;
    }

    const requestMessagePending = trackingStore.requests.find(
      ({ type, status: { code = null } = {} }) =>
        type === requestType && code === ORDER_REQUEST_STATUS.PENDING_TREATMENT
    );
    const { id: deliveryRequestId = null } = requestMessagePending || {};

    return deliveryRequestId;
  }

  /**
   * Handle confirm.
   */
  async handleConfirm() {
    const { type } = this.$route.params || {};

    this.isDeliveryRequestCanceledSuccessfully = true;

    try {
      const deliveryRequestId = this.handleFindRequestId({ type });

      if (!deliveryRequestId) {
        throw new BaseError(
          'RequestCancelScene',
          'handleConfirm',
          'Trying to cancel request without request id'
        );
      }

      const { result: isCanceled = false } = await orderRequestService.cancel(
        deliveryRequestId
      );

      if (isCanceled) {
        switch (type) {
          // if canceling contact type.
          case 'contact':
            trackingStore.setRequestPendingTreatment({
              key: 'contact',
              value: false
            });
            break;

          // if canceling delivery request type.
          case 'request':
            trackingStore.setRequestPendingTreatment({
              key: 'changeAddress',
              value: false
            });

            trackingStore.setRequestPendingTreatment({
              key: 'changeDate',
              value: false
            });

            trackingStore.setRequestPendingTreatment({
              key: 'changeDateAndAddress',
              value: false
            });
            break;
        }

        this.$off('trackingDataUpdated');

        // Get data to update history.
        await this.handleGetDeliveryData();

        this.$router.push({
          name: 'Tracking',
          params: {
            key: this.key
          }
        });
      } else {
        this.isSnackbarVisible = true;
        this.isDeliveryRequestCanceledSuccessfully = false;
      }
    } catch (error) {
      this.isSnackbarVisible = true;
      this.isDeliveryRequestCanceledSuccessfully = false;

      if (process.env.BUILD_MODE !== 'prd') {
        console.error(error);
      }
    }
  }

  /**
   * Handle cancel.
   */
  handleCancel() {
    this.$router.push({
      name: 'Tracking',
      params: {
        key: this.key
      }
    });
  }
}
